// src/pages/processifyd/ProcessifydErrorMessages.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydErrorMessages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingError, setEditingError] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    errorMessageNumber: "",
    errorMessageCondition: "",
    errorMessage: "",
    helpID: "",
    client: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoErrorMessages = [
    {
      id: 1,
      errorMessageNumber: "89898989",
      errorMessageCondition: "Unsuccessful Due to Record Already Exists",
      errorMessage: "Unsuccessful!! adding the record for <<Record Name>>",
      helpID: "2000001234",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      errorMessageNumber: "90000123",
      errorMessageCondition: "Invalid Login Credentials",
      errorMessage: "Unsuccessful!! Invalid username or password. Please try again.",
      helpID: "2000005678",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "18/01/24 11:45",
      lastUpdatedBy: "Priya Sharma",
      lastUpdateDate: "05/03/24 16:20",
    },
  ];

  const loadSample = () => {
    setErrorMessages(demoErrorMessages);
  };

  const handleAddNew = () => {
    setEditingError(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (err) => {
    setEditingError(err);
    setFormData({
      errorMessageNumber: err.errorMessageNumber,
      errorMessageCondition: err.errorMessageCondition || "",
      errorMessage: err.errorMessage || "",
      helpID: err.helpID || "",
      client: err.client || "",
      active: err.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this error message? This action cannot be undone.")) return;
    setErrorMessages((prev) => prev.filter((e) => e.id !== id));
  };

  const handleOpenErrorMessage = (err) => {
    // Update this route as per your actual error message detail page
    navigate(`/processifyd-errormessagehub/${err.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.errorMessageNumber.trim() || !formData.errorMessage.trim()) {
      alert("Error Message Number and Error Message are required.");
      return;
    }

    const newError = {
      id: editingError?.id || Date.now(),
      ...formData,
      createdBy: editingError?.createdBy || "Sanjeev Kumar",
      createdDate: editingError?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingError) {
      setErrorMessages((prev) =>
        prev.map((e) => (e.id === editingError.id ? newError : e))
      );
    } else {
      setErrorMessages((prev) => [...prev, newError]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = errorMessages.filter(
    (e) =>
      e.errorMessageNumber.toLowerCase().includes(search.toLowerCase()) ||
      e.errorMessageCondition.toLowerCase().includes(search.toLowerCase()) ||
      e.errorMessage.toLowerCase().includes(search.toLowerCase()) ||
      e.client.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            ⚠️ Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Error Messages Master
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">⏻</button>
            </div>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample Error Messages
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Error Message
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by error number, condition or message..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Error Message Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((err) => (
            <div
              key={err.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors">
                    Error #{err.errorMessageNumber}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1 line-clamp-2">
                    {err.errorMessageCondition}
                  </div>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium ${err.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {err.active === "Yes" ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-4">
                {err.errorMessage}
              </p>

              <div className="mt-4 text-xs text-gray-500">
                <strong>Help ID:</strong> {err.helpID || "—"}<br />
                <strong>Client:</strong> {err.client}
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenErrorMessage(err)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(err)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Error Message"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(err.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Error Message"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No error messages match your search" : "No error messages yet. Click 'New Error Message' or 'Load Sample Error Messages'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingError ? "Edit Error Message" : "New Error Message"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Error Message Number <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.errorMessageNumber}
                    onChange={(e) => setFormData({ ...formData, errorMessageNumber: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. 89898989"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Error Message Condition</label>
                  <input
                    value={formData.errorMessageCondition}
                    onChange={(e) => setFormData({ ...formData, errorMessageCondition: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Unsuccessful Due to Record Already Exists"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Error Message <span className="text-red-400">*</span></label>
                  <textarea
                    required
                    value={formData.errorMessage}
                    onChange={(e) => setFormData({ ...formData, errorMessage: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Unsuccessful!! adding the record for <<Record Name>>"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Help ID</label>
                    <input
                      value={formData.helpID}
                      onChange={(e) => setFormData({ ...formData, helpID: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. 2000001234"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Client <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.client}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. 21st Century Financial Services"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingError ? "Update Error Message" : "Create Error Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydErrorMessages;